import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, TextInput, TypeaheadInput, type SearchOption } from "@jsq/ds/core";
import { formatIsoDate, parseIsoDate } from "../DatePeriodSection/dateHelpers";
import * as styles from "./styles_vanilla.css";
import { isSetOperator, type FilterOperator, type RuleClauseValue } from "./types";
import { useReportQueryFieldValues } from "./useReportQueryFieldValues";
import { isClauseValueFilled } from "./validation";
import type { AttributeDataType, AttributeLeaf } from "./attributeCatalogue";

type RuleValueEditorProps = {
  clauseId: string;
  attribute: AttributeLeaf | undefined;
  operator: FilterOperator;
  value: RuleClauseValue;
  showError: boolean;
  isReadOnly: boolean;
  arenaId?: number | null;
  onChange: (next: RuleClauseValue) => void;
};

type RemoteValuePickerProps = {
  arenaId: number | null;
  attributePath: string;
  isSet: boolean;
  selectedValue: string;
  valueError: string | undefined;
  testId: string;
  onChange: (next: RuleClauseValue) => void;
};

const ERROR_TEXT = "Select a value";

/** Splits a free-form set-operator string into the underlying array. We
 * accept commas, newlines, and tabs; trim whitespace; and drop empties so
 * trailing commas don't leak `""` entries into the payload. */
const parseDelimited = (raw: string): string[] =>
  raw
    .split(/[,\n\t]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

const joinDelimited = (values: string[]): string => values.join(", ");

const toSearchOptions = (
  options: ReadonlyArray<{ value: string; label: string }>,
): SearchOption[] =>
  options.map((option) => ({
    id: option.value,
    label: option.label,
  }));

const EMPTY_TYPEAHEAD_SEARCH_VALUE = " ";

const placeholderForDataType = (
  dataType: AttributeDataType | undefined,
  isSet: boolean,
): string => {
  if (isSet) return "Enter comma-separated values";
  if (dataType === "NUMBER" || dataType === "CURRENCY" || dataType === "PERCENTAGE") {
    return "Enter a number";
  }
  if (dataType === "TIME" || dataType === "DATE") return "YYYY-MM-DD";
  return "Enter value";
};

const RemoteValuePicker = ({
  arenaId,
  attributePath,
  isSet,
  selectedValue,
  valueError,
  testId,
  onChange,
}: RemoteValuePickerProps) => {
  const [hasOpenedRemotePicker, setHasOpenedRemotePicker] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const fieldValues = useReportQueryFieldValues({
    arenaId,
    field: attributePath,
    search,
    enabled: hasOpenedRemotePicker,
  });
  const searchOptions = useMemo(
    () => toSearchOptions(fieldValues.options),
    [fieldValues.options],
  );
  const isRemotePickerLoading =
    hasOpenedRemotePicker && (fieldValues.isLoading || !fieldValues.hasLoaded);
  const remoteError = fieldValues.error?.message;
  const errorMessage = valueError ?? remoteError;

  const handleDebouncedSearch = useCallback((nextSearch: string) => {
    setSearch(nextSearch);
  }, []);

  const handleRemotePickerFocus = useCallback(() => {
    setHasOpenedRemotePicker(true);
  }, []);

  const handleSelect = useCallback(
    (option: SearchOption) => {
      const selectedValue = String(option.id);
      onChange(isSet ? [selectedValue] : selectedValue);
    },
    [isSet, onChange],
  );

  return (
    <TypeaheadInput
      key={isSet ? attributePath : `${attributePath}:${selectedValue}`}
      options={searchOptions}
      onSelectOption={handleSelect}
      initialSearchValue={selectedValue || EMPTY_TYPEAHEAD_SEARCH_VALUE}
      onDebouncedSearch={handleDebouncedSearch}
      onFocus={handleRemotePickerFocus}
      minLength={0}
      placeholder="Search values"
      errorMessage={errorMessage}
      isLoading={isRemotePickerLoading}
      isError={fieldValues.error !== null}
      keepMenuOpenWhileLoading
      enablePagination
      hasMore={fieldValues.hasMore}
      isPaginationLoading={fieldValues.isLoadingMore}
      onFetchMore={() => void fieldValues.loadMore()}
      noResultsFoundLabel="No match found"
      isClearable
      onClearValue={() => onChange(isSet ? [] : null)}
      isFullWidth
      data-testid={testId}
    />
  );
};

/** Branches the value editor on `operator × attribute.dataType × catalogue`.
 *
 *  - Remote value catalogue → DS `TypeaheadInput` with server-side search.
 *  - No catalogue + scalar op → `TextInput` (placeholder hints type).
 *  - No catalogue + `IN`/`NOT_IN` → `TextInput` with comma-separated parsing.
 * The empty-state error only flips after the form is submitted (the parent
 * passes `showError = submitCount > 0`).
 */
export const RuleValueEditor = ({
  clauseId,
  attribute,
  operator,
  value,
  showError,
  isReadOnly,
  arenaId,
  onChange,
}: RuleValueEditorProps) => {
  const isSet = isSetOperator(operator);
  const isEmpty = !isClauseValueFilled(value);
  const validationError = showError && isEmpty ? ERROR_TEXT : undefined;
  const testId = `rule-value-${clauseId}`;
  const attributePath = attribute?.path ?? null;
  const canFetchFieldValues = arenaId !== undefined && attributePath != null;
  const shouldUseRemotePicker = canFetchFieldValues && !isReadOnly;
  const valueError = validationError;

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      onChange(formatIsoDate(date) ?? "");
    },
    [onChange],
  );

  // The set-operator input keeps a *local* string buffer (`setBuffer`) for
  // what the user sees, but commits the parsed `string[]` to Formik on
  // every keystroke. Why both:
  //
  //  - If we ONLY round-tripped through Formik, mid-typing tokens like
  //    `"1,"` would parse to `["1"]`, join back to `"1"`, and the comma
  //    would disappear before the user typed the next character.
  //
  //  - If we ONLY committed on blur, a keyboard submit (Enter while the
  //    input stays focused) would fire `onSubmit` with stale form state.
  //
  // The buffer drives display so commas / whitespace survive while the
  // user types; Formik mirrors the parsed array so submit is always
  // up-to-date regardless of focus. `committedRef` lets us tell apart "I
  // just typed in this field" (display normalized differently from form
  // state — leave alone) from "operator / attribute reset cleared the
  // form state externally" (re-sync the buffer).
  const [setBuffer, setSetBuffer] = useState(() =>
    Array.isArray(value) ? joinDelimited(value) : "",
  );
  const committedRef = useRef<string>(setBuffer);

  useEffect(() => {
    if (!isSet) return;
    const next = Array.isArray(value) ? joinDelimited(value) : "";
    if (next !== committedRef.current) {
      committedRef.current = next;

      setSetBuffer(next);
    }
  }, [isSet, value]);

  const handleSetTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      setSetBuffer(raw);
      const parsed = parseDelimited(raw);
      committedRef.current = joinDelimited(parsed);
      onChange(parsed);
    },
    [onChange],
  );

  const renderControl = (): ReactNode => {
    if (shouldUseRemotePicker) {
      return (
        <RemoteValuePicker
          key={attributePath}
          arenaId={arenaId ?? null}
          attributePath={attributePath}
          isSet={isSet}
          selectedValue={!isSet && typeof value === "string" ? value : ""}
          valueError={valueError}
          testId={testId}
          onChange={onChange}
        />
      );
    }

    const placeholder = placeholderForDataType(attribute?.dataType, isSet);

    if (isSet) {
      return (
        <TextInput
          value={setBuffer}
          onChange={handleSetTextChange}
          placeholder={placeholder}
          aria-label="Values"
          errorMessage={valueError}
          data-testid={testId}
          disabled={isReadOnly}
          isFullWidth
        />
      );
    }

    if (attribute?.dataType === "TIME" || attribute?.dataType === "DATE") {
      return (
        <div data-testid={testId}>
          <DatePicker
            isFull
            hasIcon
            disabled={isReadOnly}
            selected={parseIsoDate(typeof value === "string" ? value : null)}
            onChange={handleDateChange}
            placeholderText="Select date"
            errorMessage={valueError}
            autoComplete="off"
          />
        </div>
      );
    }

    return (
      <TextInput
        value={typeof value === "string" ? value : ""}
        onChange={handleTextChange}
        placeholder={placeholder}
        aria-label="Value"
        errorMessage={valueError}
        data-testid={testId}
        disabled={isReadOnly}
        isFullWidth
      />
    );
  };

  return <div className={styles.valueSelect}>{renderControl()}</div>;
};
